import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { CountrySelect } from 'components/ui/country-select';
import { Form, FormField, FormItem, FormControl, FormMessage } from 'components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'components/ui/command';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from 'lib/utils';
import { ACCOUNT_QUERY_KEY, useCreateAccount } from 'features/profile/hooks/use-account';
import { useOrganizations } from 'features/organization/hooks/use-organization';
import { useGetRolesQuery } from 'features/iam/hooks/use-iam';
import { Badge } from 'components/ui/badge';
import { useTranslation } from 'react-i18next';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { ROLES } from 'constant/roles';
import { ORGANIZATION_CONSTANTS } from 'constant/organization';

/**
 * AddUser Component
 *
 * A dialog component that displays a form for creating a new user account. On successful submission,
 * it invalidates and refetches account-related queries and reloads the page to reflect the changes.
 *
 * Features:
 * - Uses `react-hook-form` for form state and validation
 * - Submits form data via `useCreateAccount` mutation
 * - Auto-refreshes relevant queries and reloads the window on success
 * - Provides input fields for first name, last name, email, and organization selection
 * - Organization autocomplete with search functionality
 * - Styled using Tailwind and ShadCN dialog components
 *
 * Props:
 * - `onClose` (function): Callback to close the dialog
 *
 * @param {AddUserProps} props - Component props
 * @returns {JSX.Element} A modal with a user invitation form
 *
 * @example
 * <AddUser onClose={() => setIsDialogOpen(false)} />
 */

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  country: string;
  salutation: string;
  organizationId: string;
  roles: string[];
  currentRole: string;
}

type AddUserProps = {
  onClose: () => void;
};

const MAX_ROLES = 3;

export const AddUser: React.FC<AddUserProps> = ({ onClose }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [organizationSearchTerm, setOrganizationSearchTerm] = useState('');
  const [organizationOpen, setOrganizationOpen] = useState(false);
  const isAdmin = useCommonStore((state) => state.isAdmin);
  const isOrgAdmin = useCommonStore((state) => state.isOrgAdmin);
  const [availableRoles, setAvailableRoles] = useState<
    Array<{
      name: string;
      slug: string;
    }>
  >([]);

  const { data: rolesData, isLoading: isLoadingRoles } = useGetRolesQuery({
    page: 0,
    pageSize: 100,
    filter: { search: '' },
    sort: { property: 'name', isDescending: false },
  });

  // Fetch organizations for autocomplete
  const { data: organizationsData } = useOrganizations({
    page: 0,
    pageSize: 50,
    name: organizationSearchTerm,
    enabled: true,
  });

  const organizations = (organizationsData?.data || []).filter(
    org => org.name !== ORGANIZATION_CONSTANTS.DEFAULT_ORGANIZATION_NAME
  );


  useEffect(() => {
    if (rolesData?.data) {
      const mappedRoles = rolesData.data
        .filter(role => {
          if (isOrgAdmin) {
            // remove app-user and admin roles for org-admin users from UI
            return role.name !== 'app-user' && role.name !== 'admin';
          }
          // remove app-user role for admin users from UI
          return role.name !== 'app-user';
        })
        .map((role) => ({
          name: role.name,
          slug: role.name.toLowerCase().replace(/\s+/g, '-'),
        }));
      if (mappedRoles) setAvailableRoles(mappedRoles);
    }
  }, [isOrgAdmin, rolesData]);

  const { mutate: createAccount } = useCreateAccount({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEY });

      await queryClient.refetchQueries({
        queryKey: ACCOUNT_QUERY_KEY,
        type: 'active',
        exact: false,
      });

      onClose();
      window.location.reload();
    },
  });

  const form = useForm<FormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      country: '',
      salutation: '',
      organizationId: '',
      roles: [],
      currentRole: '',
    },
  });

  const { control, handleSubmit, setValue, watch } = form;
  const selectedOrganizationId = watch('organizationId');
  const selectedOrganization = organizations.find(org => org.itemId === selectedOrganizationId);
  const watchedValues = watch();
  const isOrgAdminOrUser = watchedValues.roles?.includes(ROLES.ORG_ADMIN) || watchedValues.roles?.includes(ROLES.ORG_USER);


  const onSubmit = async (data: FormData) => {
    const tempRoles = data.roles ?? [];
    tempRoles.push('app-user');

    const payload = {
      itemId: '',
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      country: data.country,
      userPassType: 1,
      userCreationType: 1,
      platform: 'blocks_portal',
      organizationIds: isAdmin ? (data.organizationId == "" ? [] : [data.organizationId]) : organizationsData?.data?.map((org) => org.itemId),
      roles: tempRoles,
    };

    createAccount(payload);
  };

  const handleAddRole = (roleSlug: string) => {
    if (!roleSlug) return;

    const currentRoles = form.getValues('roles') || [];

    if (currentRoles.length >= MAX_ROLES) return;

    if (!currentRoles.includes(roleSlug)) {
      setValue('roles', [...currentRoles, roleSlug]);
    }
    setValue('currentRole', '');
  };

  const handleRemoveRole = (roleToRemove: string) => {
    const currentRoles = form.getValues('roles') || [];
    setValue(
      'roles',
      currentRoles.filter((role) => role !== roleToRemove)
    );
  };

  const getAvailableRoles = () => {
    const selectedRoles = form.getValues('roles') || [];
    return availableRoles.filter((role) => !selectedRoles.includes(role.slug));
  };

  /* const getRoleNameBySlug = (slug: string) => {
    const role = availableRoles.find((r) => r.slug === slug);
    return role ? role.name : slug;
  }; */

  const isMaxRolesReached = () => {
    const selectedRoles = form.getValues('roles') || [];
    return selectedRoles.length >= MAX_ROLES;
  };

  const handleDialogClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  return (
    <DialogContent
      className=" sm:max-w-[700px] overflow-y-auto max-h-screen"
      onClick={handleDialogClick}
    >
      <DialogHeader>
        <DialogTitle className="mb-2">{t('ADD_USER')}</DialogTitle>
        <DialogDescription className="text-medium-emphasis font-normal">
          {t('ADD_USER_DESCRIPTION')}
        </DialogDescription>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={control}
              name="firstName"
              rules={{
                required: t('FIRST_NAME_REQUIRED'),
                maxLength: {
                  value: 15,
                  message: t('MAX_15_CHARACTERS')
                }
              }}
              render={({ field }) => (
                <FormItem>
                  <Label>{t('FIRST_NAME')}*</Label>
                  <FormControl>
                    <Input {...field} placeholder={t('ENTER_YOUR_FIRST_NAME')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="lastName"
              rules={{
                required: t('LAST_NAME_REQUIRED'),
                maxLength: {
                  value: 15,
                  message: t('MAX_15_CHARACTERS')
                }
              }}
              render={({ field }) => (
                <FormItem>
                  <Label>{t('LAST_NAME')}*</Label>
                  <FormControl>
                    <Input {...field} placeholder={t('ENTER_YOUR_LAST_NAME')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={control}
              name="email"
              rules={{ required: t('EMAIL_REQUIRED'), pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t('ENTER_VALID_EMAIL') } }}
              render={({ field }) => (
                <FormItem>
                  <Label>{t('EMAIL')}*</Label>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t('ENTER_YOUR_EMAIL_ADDRESS')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="country"
              rules={{ required: t('COUNTRY_REQUIRED') || 'Country is required' }}
              render={({ field }) => (
                <FormItem>
                  <Label>{t('COUNTRY')}*</Label>
                  <FormControl>
                    <CountrySelect
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={t('SELECT_COUNTRY') || 'Select country'}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={control}
              name="currentRole"
              rules={{ required: isAdmin || watchedValues.roles.length > 0 ? false : t('ROLES_REQUIRED') }}
              render={({ field }) => (
                <FormItem>
                  <Label>
                    {t('ROLES')}{isOrgAdmin ? "*" : ""}
                  </Label>
                  <div className="space-y-2">
                    <Select
                      onValueChange={(value) => {
                        if (value == "admin" && isAdmin) {
                          setValue('organizationId', '');
                        }
                        handleAddRole(value);
                      }}
                      value={field.value}
                      disabled={isMaxRolesReached() || isLoadingRoles}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              isLoadingRoles
                                ? `${t('LOADING_ROLES')}...`
                                : isMaxRolesReached()
                                  ? t('MAX_ROLES_REACHED')
                                  : t('SELECT_ROLES')
                            }
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {getAvailableRoles().map((role) => (
                          <SelectItem key={role.slug} value={role.slug}>
                            <span>{t(role.slug?.toUpperCase())}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <div className="flex flex-wrap gap-2 mt-2">
                      {watchedValues.roles?.map((roleSlug) => (
                        <Badge
                          key={roleSlug}
                          className="pr-1 flex items-center gap-1 text-white hover:bg-primary"
                        >
                          <span>{t(roleSlug?.toUpperCase())}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            className="h-4 w-4 p-0 hover:bg-transparent"
                            onClick={() => handleRemoveRole(roleSlug)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </Badge>
                      ))}
                    </div>
                    {/* {watchedValues.roles?.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {watchedValues.roles.length} of {MAX_ROLES} {t('ROLES_SELECTED')}
                      </p>
                    )} */}
                  </div>
                  <FormMessage className="mt-0" />
                </FormItem>
              )}
            />

            {isAdmin && (
              <FormField
                control={control}
                name="organizationId"
                rules={{ required: isOrgAdminOrUser ? t('ORG_ADMIN_ORG_USER_ORG_REQUIRED') : false }}
                render={({ field }) => (
                  <FormItem>
                    <Label className="text-high-emphasis">{t('ORGANIZATION')} {isOrgAdminOrUser ? "*" : ""}</Label>
                    {watchedValues.roles?.includes(ROLES.ADMIN) && (
                      <p className="text-sm text-yellow-600 mb-2">
                        {t('ADMIN_ORG_WARNING')}
                      </p>
                    )}
                    <Popover open={organizationOpen} onOpenChange={setOrganizationOpen}>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={organizationOpen}
                            className={cn(
                              "w-full justify-between h-11",
                              !field.value && "text-muted-foreground"
                            )}
                            disabled={watchedValues.roles?.includes(ROLES.ADMIN) || !watchedValues.roles?.length}
                          >
                            {selectedOrganization ? selectedOrganization.name : t('SELECT_ORGANIZATION')}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-[400px] p-0" align="start">
                        <Command className="overflow-hidden">
                          <CommandInput
                            placeholder={t('SEARCH_ORGANIZATIONS')}
                            value={organizationSearchTerm}
                            onValueChange={setOrganizationSearchTerm}
                          />
                          <CommandList className="h-[200px] overflow-auto py-2">
                            <CommandEmpty>{t('NO_ORGANIZATIONS_FOUND')}</CommandEmpty>
                            <CommandGroup>
                              {organizations.map((organization) => (
                                <CommandItem
                                  key={organization.itemId}
                                  value={organization.name}
                                  onSelect={() => {
                                    setValue('organizationId', organization.itemId);
                                    setOrganizationOpen(false);
                                  }}
                                  className="cursor-pointer"
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4 shrink-0",
                                      selectedOrganizationId === organization.itemId ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  <div className="flex flex-col min-w-0 flex-1">
                                    <span className="font-medium truncate">{organization.name}</span>
                                    <span className="text-sm text-muted-foreground truncate">{organization.email}</span>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>
          <DialogFooter className="mt-5 flex justify-end gap-2">
            <Button variant="outline" type="button" onClick={onClose}>
              {t('CANCEL')}
            </Button>
            <Button type="submit">{t('INVITE_USER')}</Button>
          </DialogFooter>
        </form>
      </Form>
    </DialogContent>
  );
};
